import React, { useContext, useEffect, useMemo, useState } from 'react';
import { SelectItemType } from '../Framework/Types/FormTypes';
import { SendsPageDataType, useSendsPageDetail } from '../Api/StandaloneSendsEndpoints';
import { useParams } from 'react-router-dom';
import { GetObjectIndexByKeyValue } from '../Framework/Constants/Helpers';

interface SendsDetailAddressPostage {
	id: number;
	value: string;
}

interface SendsDetailAddress extends SendsDetailAddressPostage {
	postageOptions: Array<SendsDetailAddressPostage>;
}

interface SendMailProviderType {
	address: SendsDetailAddress | null;
	postage: SendsDetailAddressPostage | null;
	sendPageID: number;
	file: any;
	reset: boolean;

	addressOptions: Array<SelectItemType>;
	postageOptions: Array<SelectItemType>;

	UpdatePageId: (v: number) => void;
	UpdateAddress: (v: number, newAddress?: boolean) => void;
	UpdatePostage: (v: number) => void;
	UpdateFile: (v: any) => void;
	ResetSendMail: () => void;

	sendPageLoading: boolean;
	sendPageData: SendsPageDataType;
	sendPageError: any;
}

const SendMailContext = React.createContext<SendMailProviderType>({} as SendMailProviderType);

const SendMailProvider = ({ children }: { children: any }) => {
	const { pageId } = useParams();

	const [sendPageID, setSendPageID] = useState<number>(parseInt(pageId as string));

	const [address, setAddress] = useState<SendsDetailAddress | null>(null);
	const [postage, setPostage] = useState<SendsDetailAddressPostage | null>(null);
	const [file, setFile] = useState<any>(null);

	const [reset, setReset] = useState<boolean>(false);

	const {
		isLoading: sendPageLoading,
		data: sendPageData,
		error: sendPageError,
	}: {
		isLoading: boolean;
		data: any;
		error: any;
	} = useSendsPageDetail(sendPageID);

	const [addressOptions, setAddressOptions] = useState<Array<SelectItemType>>([]);
	const [postageOptions, setPostageOptions] = useState<Array<SelectItemType>>([]);

	useEffect(() => {
		ResetSendMail();
	}, [sendPageID]);

	useEffect(() => {
		if (!sendPageLoading) {
			MapAddressData();
		}
	}, [reset, sendPageLoading]);

	useEffect(() => {
		if (!sendPageLoading && address) {
			MapPostageData();
		}
	}, [address, sendPageLoading]);

	const MapAddressData = (addressId?: number) => {
		let filteredAddress: Array<any> = [];

		sendPageData?.addresses &&
			sendPageData.addresses.forEach((address: SendsDetailAddress, index: number) => {
				filteredAddress.push({ label: address.value, value: address.id });
			});

		if (filteredAddress.length === 1) {
			UpdateAddress(filteredAddress[0].value);
		}

		setAddressOptions(filteredAddress);
	};

	const MapPostageData = () => {
		let filteredPostage: Array<any> = [];

		if (address && sendPageData.addresses) {
			const addressIndex = GetObjectIndexByKeyValue(sendPageData.addresses, 'value', address.value);

			sendPageData.addresses[addressIndex].postageOptions.forEach(
				(postage: SendsDetailAddressPostage, index: number) => {
					filteredPostage.push({ label: postage.value, value: postage.id });
				}
			);

			if (filteredPostage.length === 1) {
				UpdatePostage(filteredPostage[0].value);
			}

			setPostageOptions(filteredPostage);
		}
	};

	const ResetPostage = () => {
		setPostageOptions([]);
		setPostage(null);
	};

	const ResetSendMail = () => {
		ResetPostage();
		setAddress(null);
		setFile(null);
		setReset(!reset);
	};

	const UpdatePageId = (value: number) => {
		setSendPageID(value);
	};

	const UpdateAddress = (addressId: number, newAddress: boolean = false) => {
		const newActive = sendPageData.addresses.filter(
			(address: SendsDetailAddress, index: number) => address.id === addressId
		)[0];

		if (newAddress) {
			MapAddressData();
		}

		setAddress(newActive);
	};

	const UpdatePostage = (postageId: number) => {
		const addressIndex = GetObjectIndexByKeyValue(sendPageData.addresses, 'value', address?.value);
		const newActive = sendPageData.addresses[addressIndex].postageOptions.filter(
			(postage: SendsDetailAddressPostage, index: number) => postage.id === postageId
		)[0];
		setPostage(newActive);
	};

	const UpdateFile = (value: any) => {
		setFile(value);
	};

	useEffect(() => {
		if (pageId) {
			UpdatePageId(parseInt(pageId as string));
		}
	}, [pageId]);

	const sendMailMemo = useMemo(() => {
		return {
			address,
			postage,
			sendPageID,
			file,
			reset,

			addressOptions,
			postageOptions,

			sendPageLoading,
			sendPageData,
			sendPageError,

			UpdatePageId,
			UpdateAddress,
			UpdatePostage,
			UpdateFile,
			ResetSendMail,
		};
	}, [
		address,
		postage,
		sendPageID,
		file,
		reset,

		addressOptions,
		postageOptions,

		sendPageLoading,
		sendPageData,
		sendPageError,
	]);

	return <SendMailContext.Provider value={sendMailMemo}>{children}</SendMailContext.Provider>;
};

const useSendMail = () => useContext(SendMailContext);

export { SendMailContext, useSendMail };

export default SendMailProvider;
